import { useState, type ReactNode, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User as UserIcon, Users, Palette, Key, Webhook, Gift, AlertTriangle,
  Copy, Trash2, Plus, X, Loader2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import i18n from '../i18n';

type TabKey =
  | 'profile' | 'team' | 'whitelabel' | 'apikeys' | 'webhooks' | 'referral' | 'danger';

interface Profile {
  email: string;
  fullName: string;
  telegramUsername: string;
  language: 'ru' | 'en';
}
interface Member {
  id: string;
  email: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  joinedAt: string;
}
interface Invitation {
  id: string;
  email: string;
  role: string;
  createdAt: string;
}
interface WhiteLabel {
  domain: string;
  logoUrl: string;
  colorPrimary: string;
  colorAccent: string;
  emailTemplates: string;
}
interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
}
interface NewApiKey extends ApiKey {
  fullKey: string;
}
interface WebhookItem {
  id: string;
  url: string;
  events: string[];
  lastTriggeredAt: string | null;
  active: boolean;
}
interface ReferralStats {
  code: string;
  referredCount: number;
  earnedUsd: number;
  payouts: Array<{ id: string; amountUsd: number; paidAt: string }>;
}

const ALL_SCOPES = ['read:posts', 'write:posts', 'read:analytics', 'manage:channels', 'admin:all'];
const ALL_EVENTS = ['post.published', 'post.failed', 'channel.connected', 'subscriber.joined', 'farm.account.banned'];

function planAllowsTeam(plan?: string): boolean {
  return plan === 'agency' || plan === 'enterprise';
}
function planAllowsWhiteLabel(plan?: string): boolean {
  return plan === 'agency' || plan === 'enterprise';
}

export function Settings(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [active, setActive] = useState<TabKey>('profile');

  const tabs: Array<{ key: TabKey; label: string; icon: typeof UserIcon; visible: boolean }> = [
    { key: 'profile',    label: t('settings.tabs.profile'),    icon: UserIcon,        visible: true },
    { key: 'team',       label: t('settings.tabs.team'),       icon: Users,           visible: planAllowsTeam(user?.plan) },
    { key: 'whitelabel', label: t('settings.tabs.whitelabel'), icon: Palette,         visible: planAllowsWhiteLabel(user?.plan) },
    { key: 'apikeys',    label: t('settings.tabs.apikeys'),    icon: Key,             visible: true },
    { key: 'webhooks',   label: t('settings.tabs.webhooks'),   icon: Webhook,         visible: true },
    { key: 'referral',   label: t('settings.tabs.referral'),   icon: Gift,            visible: true },
    { key: 'danger',     label: t('settings.tabs.danger'),     icon: AlertTriangle,   visible: true },
  ];

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-bold text-brand-dark mb-6">{t('settings.title')}</h1>
      <div role="tablist" className="flex gap-2 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.filter((tab) => tab.visible).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            role="tab"
            aria-selected={active === key}
            onClick={() => setActive(key)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition',
              active === key
                ? 'border-brand-primary text-brand-primary'
                : 'border-transparent text-brand-muted hover:text-brand-ink',
            )}
          >
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {active === 'profile'    && <ProfileTab />}
      {active === 'team'       && <TeamTab />}
      {active === 'whitelabel' && <WhiteLabelTab />}
      {active === 'apikeys'    && <ApiKeysTab />}
      {active === 'webhooks'   && <WebhooksTab />}
      {active === 'referral'   && <ReferralTab />}
      {active === 'danger'     && <DangerZoneTab />}
    </div>
  );
}

/* ===================== PROFILE ===================== */

function ProfileTab(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const q = useQuery<Profile>({
    queryKey: ['profile'],
    queryFn: async () => (await api.get<Profile>('/users/profile')).data,
    retry: false,
  });

  // Pre-fill from Firebase email if profile load fails
  const firebaseEmail = user?.email ?? '';

  const [fullName, setFullName] = useState('');
  const [telegram, setTelegram] = useState('');
  const [language, setLanguage] = useState<'ru' | 'en'>('ru');
  const [synced, setSynced] = useState(false);

  if (q.data && !synced) {
    setFullName(q.data.fullName ?? '');
    setTelegram(q.data.telegramUsername ?? '');
    setLanguage(q.data.language ?? 'ru');
    setSynced(true);
  }

  const mut = useMutation({
    mutationFn: async (payload: Partial<Profile>) =>
      (await api.patch<Profile>('/users/profile', payload)).data,
    onSuccess: (data) => {
      toast.success(t('settings.profile.saved'));
      void i18n.changeLanguage(data.language);
      qc.setQueryData(['profile'], data);
    },
    onError: () => toast.error(t('common.error')),
  });

  if (q.isLoading) return <Loader />;

  // On error: show empty form pre-filled with Firebase email
  const displayEmail = q.isError ? firebaseEmail : (q.data?.email ?? firebaseEmail);

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    mut.mutate({ fullName, telegramUsername: telegram, language });
  };

  return (
    <form className="card max-w-xl space-y-4" onSubmit={onSubmit}>
      <Field label={t('settings.profile.email')}>
        <input className="input w-full bg-slate-100" value={displayEmail} readOnly />
      </Field>
      <Field label={t('settings.profile.fullName')}>
        <input
          className="input w-full"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </Field>
      <Field label={t('settings.profile.telegram')}>
        <input
          className="input w-full"
          placeholder="@username"
          value={telegram}
          onChange={(e) => setTelegram(e.target.value)}
        />
      </Field>
      <Field label={t('settings.profile.language')}>
        <select
          className="input w-full"
          value={language}
          onChange={(e) => setLanguage(e.target.value as 'ru' | 'en')}
        >
          <option value="ru">Русский</option>
          <option value="en">English</option>
        </select>
      </Field>
      <button type="submit" className="btn-primary" disabled={mut.isPending}>
        {mut.isPending ? t('common.loading') : t('common.save')}
      </button>
    </form>
  );
}

/* ===================== TEAM ===================== */

function TeamTab(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const members = useQuery<Member[]>({
    queryKey: ['team', 'members'],
    queryFn: async () => (await api.get<Member[]>('/teams/members')).data,
    retry: false,
  });
  const invitations = useQuery<Invitation[]>({
    queryKey: ['team', 'invitations'],
    queryFn: async () => (await api.get<Invitation[]>('/teams/invitations')).data,
    retry: false,
  });

  const removeMut = useMutation({
    mutationFn: async (id: string) => api.delete(`/teams/members/${id}`),
    onSuccess: () => {
      toast.success(t('settings.team.removed'));
      void qc.invalidateQueries({ queryKey: ['team', 'members'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const [inviteOpen, setInviteOpen] = useState(false);

  if (members.isLoading) return <Loader />;

  // On error: show "No team members" empty state instead of error box
  const list = (members.isError ? [] : (members.data ?? []));
  const membersFailed = members.isError;

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('settings.team.members')}</h2>
          <button className="btn-primary flex items-center gap-2" onClick={() => setInviteOpen(true)}>
            <Plus size={16} /> {t('settings.team.invite')}
          </button>
        </div>
        {list.length === 0 ? (
          <Empty msg={t('settings.team.empty')} />
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-brand-muted border-b border-slate-200">
              <tr>
                <th className="py-2">{t('settings.team.colEmail')}</th>
                <th className="py-2">{t('settings.team.colRole')}</th>
                <th className="py-2">{t('settings.team.colJoined')}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} className="border-b border-slate-100">
                  <td className="py-2">{m.email}</td>
                  <td className="py-2"><Badge>{m.role}</Badge></td>
                  <td className="py-2">{dayjs(m.joinedAt).format('YYYY-MM-DD')}</td>
                  <td className="py-2 text-right">
                    {m.role !== 'owner' && (
                      <button
                        className="text-red-600 hover:text-red-700"
                        aria-label="remove"
                        onClick={() => removeMut.mutate(m.id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!membersFailed && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">{t('settings.team.pending')}</h2>
          {(invitations.data ?? []).length === 0 ? (
            <Empty msg={t('settings.team.noPending')} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {invitations.data?.map((inv) => (
                <li key={inv.id} className="py-2 flex justify-between text-sm">
                  <span>{inv.email}</span>
                  <Badge>{inv.role}</Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'editor' | 'viewer'>('editor');

  const mut = useMutation({
    mutationFn: async () => api.post('/teams/invite', { email, role }),
    onSuccess: () => {
      toast.success(t('settings.team.invited'));
      void qc.invalidateQueries({ queryKey: ['team', 'invitations'] });
      onClose();
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <Modal onClose={onClose} title={t('settings.team.invite')}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
      >
        <Field label="Email">
          <input
            type="email" required
            className="input w-full"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t('settings.team.colRole')}>
          <select className="input w-full" value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            <option value="admin">admin</option>
            <option value="editor">editor</option>
            <option value="viewer">viewer</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={mut.isPending}>{t('settings.team.send')}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ===================== WHITE LABEL ===================== */

function WhiteLabelTab(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery<WhiteLabel>({
    queryKey: ['whitelabel'],
    queryFn: async () => (await api.get<WhiteLabel>('/teams/white-label')).data,
  });

  const [form, setForm] = useState<WhiteLabel | null>(null);
  if (q.data && !form) setForm(q.data);

  const mut = useMutation({
    mutationFn: async (payload: WhiteLabel) =>
      (await api.patch<WhiteLabel>('/teams/white-label', payload)).data,
    onSuccess: (data) => {
      toast.success(t('settings.whitelabel.saved'));
      qc.setQueryData(['whitelabel'], data);
    },
    onError: () => toast.error(t('common.error')),
  });

  if (q.isLoading || !form) return <Loader />;
  if (q.isError) return <ErrorBox />;

  const set = (patch: Partial<WhiteLabel>): void => setForm({ ...form, ...patch });

  return (
    <form
      className="card max-w-2xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate(form);
      }}
    >
      <Field label={t('settings.whitelabel.domain')}>
        <input
          className="input w-full"
          placeholder="brand.teleclaw.com"
          value={form.domain}
          onChange={(e) => set({ domain: e.target.value })}
        />
      </Field>
      <Field label={t('settings.whitelabel.logo')}>
        <input type="file" accept="image/*" className="block text-sm" />
        {form.logoUrl && <img src={form.logoUrl} alt="logo" className="mt-2 h-12" />}
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t('settings.whitelabel.primary')}>
          <input
            type="color"
            className="h-10 w-full rounded border border-slate-300"
            value={form.colorPrimary}
            onChange={(e) => set({ colorPrimary: e.target.value })}
          />
        </Field>
        <Field label={t('settings.whitelabel.accent')}>
          <input
            type="color"
            className="h-10 w-full rounded border border-slate-300"
            value={form.colorAccent}
            onChange={(e) => set({ colorAccent: e.target.value })}
          />
        </Field>
      </div>
      <Field label={t('settings.whitelabel.emailTpl')}>
        <textarea
          className="input w-full font-mono text-xs"
          rows={6}
          value={form.emailTemplates}
          onChange={(e) => set({ emailTemplates: e.target.value })}
        />
      </Field>
      <div className="flex gap-2">
        <button type="button" className="btn-secondary">{t('settings.whitelabel.preview')}</button>
        <button type="submit" className="btn-primary" disabled={mut.isPending}>{t('common.save')}</button>
      </div>
    </form>
  );
}

/* ===================== API KEYS ===================== */

function ApiKeysTab(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery<ApiKey[]>({
    queryKey: ['apikeys'],
    queryFn: async () => (await api.get<ApiKey[]>('/api-keys')).data,
    retry: false,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [newKey, setNewKey] = useState<NewApiKey | null>(null);

  const revoke = useMutation({
    mutationFn: async (id: string) => api.delete(`/api-keys/${id}`),
    onSuccess: () => {
      toast.success(t('settings.apikeys.revoked'));
      void qc.invalidateQueries({ queryKey: ['apikeys'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  if (q.isLoading) return <Loader />;

  // On error: show empty list with "Create your first API key" prompt
  const keys = (q.isError ? [] : (q.data ?? []));

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{t('settings.apikeys.title')}</h2>
        <button className="btn-primary flex items-center gap-2" onClick={() => setCreateOpen(true)}>
          <Plus size={16} /> {t('settings.apikeys.generate')}
        </button>
      </div>
      {keys.length === 0 ? (
        <EmptyAction
          msg={t('settings.apikeys.empty')}
          actionLabel={t('settings.apikeys.generate')}
          onAction={() => setCreateOpen(true)}
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-brand-muted border-b border-slate-200">
            <tr>
              <th className="py-2">{t('settings.apikeys.colName')}</th>
              <th className="py-2">{t('settings.apikeys.colPrefix')}</th>
              <th className="py-2">{t('settings.apikeys.colScopes')}</th>
              <th className="py-2">{t('settings.apikeys.colLastUsed')}</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id} className="border-b border-slate-100">
                <td className="py-2">{k.name}</td>
                <td className="py-2 font-mono">{k.prefix}…</td>
                <td className="py-2">
                  <div className="flex flex-wrap gap-1">
                    {k.scopes.map((s) => <Badge key={s}>{s}</Badge>)}
                  </div>
                </td>
                <td className="py-2">{k.lastUsedAt ? dayjs(k.lastUsedAt).format('YYYY-MM-DD') : '—'}</td>
                <td className="py-2 text-right">
                  <button
                    className="text-red-600 hover:text-red-700"
                    aria-label="revoke"
                    onClick={() => revoke.mutate(k.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {createOpen && (
        <CreateApiKeyModal
          onClose={() => setCreateOpen(false)}
          onCreated={(k) => { setNewKey(k); setCreateOpen(false); }}
        />
      )}
      {newKey && <ShowApiKeyModal newKey={newKey} onClose={() => setNewKey(null)} />}
    </div>
  );
}

function CreateApiKeyModal({
  onClose, onCreated,
}: { onClose: () => void; onCreated: (k: NewApiKey) => void }): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>([]);

  const mut = useMutation({
    mutationFn: async () => (await api.post<NewApiKey>('/api-keys', { name, scopes })).data,
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['apikeys'] });
      onCreated(data);
    },
    onError: () => toast.error(t('common.error')),
  });

  const toggle = (s: string): void => {
    setScopes(scopes.includes(s) ? scopes.filter((x) => x !== s) : [...scopes, s]);
  };

  return (
    <Modal onClose={onClose} title={t('settings.apikeys.generate')}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <Field label={t('settings.apikeys.colName')}>
          <input required className="input w-full" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={t('settings.apikeys.colScopes')}>
          <div className="space-y-1">
            {ALL_SCOPES.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggle(s)}
                />
                <span className="font-mono">{s}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={mut.isPending}>
            {t('settings.apikeys.generate')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ShowApiKeyModal({ newKey, onClose }: { newKey: NewApiKey; onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const copy = (): void => {
    void navigator.clipboard?.writeText(newKey.fullKey);
    toast.success(t('settings.apikeys.copied'));
  };
  return (
    <Modal onClose={onClose} title={t('settings.apikeys.created')}>
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 text-sm">
          {t('settings.apikeys.warning')}
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-slate-100 p-2 rounded text-xs break-all">{newKey.fullKey}</code>
          <button className="btn-secondary p-2" onClick={copy} aria-label="copy">
            <Copy size={16} />
          </button>
        </div>
        <div className="flex justify-end">
          <button className="btn-primary" onClick={onClose}>{t('settings.apikeys.acknowledge')}</button>
        </div>
      </div>
    </Modal>
  );
}

/* ===================== WEBHOOKS ===================== */

function WebhooksTab(): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery<WebhookItem[]>({
    queryKey: ['webhooks'],
    queryFn: async () => (await api.get<WebhookItem[]>('/webhooks')).data,
    retry: false,
  });
  const [open, setOpen] = useState(false);

  const toggle = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/webhooks/${id}`, { active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['webhooks'] }),
    onError: () => toast.error(t('common.error')),
  });
  const del = useMutation({
    mutationFn: async (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      toast.success(t('common.delete'));
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  if (q.isLoading) return <Loader />;

  // On error: show empty list with "No webhooks yet"
  const list = (q.isError ? [] : (q.data ?? []));

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">{t('settings.webhooks.title')}</h2>
        <button className="btn-primary flex items-center gap-2" onClick={() => setOpen(true)}>
          <Plus size={16} /> {t('settings.webhooks.add')}
        </button>
      </div>
      {list.length === 0 ? (
        <Empty msg={t('settings.webhooks.empty')} />
      ) : (
        <ul className="divide-y divide-slate-100">
          {list.map((w) => (
            <li key={w.id} className="py-3 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm truncate">{w.url}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {w.events.map((e) => <Badge key={e}>{e}</Badge>)}
                </div>
                <div className="text-xs text-brand-muted mt-1">
                  {w.lastTriggeredAt
                    ? `${t('settings.webhooks.lastTriggered')}: ${dayjs(w.lastTriggeredAt).format('YYYY-MM-DD HH:mm')}`
                    : t('settings.webhooks.never')}
                </div>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={w.active}
                  onChange={() => toggle.mutate({ id: w.id, active: !w.active })}
                />
                <div className="w-10 h-5 bg-slate-300 rounded-full peer-checked:bg-brand-primary relative transition">
                  <div className={clsx(
                    'absolute top-0.5 left-0.5 bg-white w-4 h-4 rounded-full transition',
                    w.active && 'translate-x-5',
                  )} />
                </div>
              </label>
              <button
                className="text-red-600 hover:text-red-700"
                aria-label="delete"
                onClick={() => del.mutate(w.id)}
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {open && <WebhookModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function WebhookModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const mut = useMutation({
    mutationFn: async () => api.post('/webhooks', { url, events }),
    onSuccess: () => {
      toast.success(t('settings.webhooks.added'));
      void qc.invalidateQueries({ queryKey: ['webhooks'] });
      onClose();
    },
    onError: () => toast.error(t('common.error')),
  });
  const toggle = (e: string): void => {
    setEvents(events.includes(e) ? events.filter((x) => x !== e) : [...events, e]);
  };
  return (
    <Modal onClose={onClose} title={t('settings.webhooks.add')}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <Field label="URL">
          <input
            type="url" required
            className="input w-full"
            placeholder="https://example.com/webhook"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>
        <Field label={t('settings.webhooks.events')}>
          <div className="space-y-1">
            {ALL_EVENTS.map((ev) => (
              <label key={ev} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={events.includes(ev)} onChange={() => toggle(ev)} />
                <span className="font-mono">{ev}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={mut.isPending}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ===================== REFERRAL ===================== */

function ReferralTab(): JSX.Element {
  const { t } = useTranslation();
  const q = useQuery<ReferralStats>({
    queryKey: ['referral'],
    queryFn: async () => (await api.get<ReferralStats>('/users/referral')).data,
    retry: false,
  });

  if (q.isLoading) return <Loader />;

  // On error: show skeleton with "Referral stats unavailable"
  if (q.isError) {
    return (
      <div className="space-y-6">
        <div className="card">
          <h2 className="text-lg font-semibold mb-2">{t('settings.referral.code')}</h2>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-slate-100 p-3 rounded h-10 animate-pulse" />
            <div className="w-10 h-10 bg-slate-100 rounded animate-pulse" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="card">
            <div className="text-brand-muted text-sm">{t('settings.referral.referred')}</div>
            <div className="h-9 bg-slate-100 rounded animate-pulse mt-1" />
          </div>
          <div className="card">
            <div className="text-brand-muted text-sm">{t('settings.referral.earned')}</div>
            <div className="h-9 bg-slate-100 rounded animate-pulse mt-1" />
          </div>
        </div>
        <div className="card">
          <p className="text-brand-muted text-sm text-center py-4">{t('settings.referral.unavailable', 'Referral stats unavailable')}</p>
        </div>
      </div>
    );
  }

  const data = q.data!;

  const copy = (): void => {
    void navigator.clipboard?.writeText(data.code);
    toast.success(t('settings.referral.copied'));
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">{t('settings.referral.code')}</h2>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-slate-100 p-3 rounded font-mono">{data.code}</code>
          <button className="btn-secondary p-2" onClick={copy} aria-label="copy">
            <Copy size={16} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="card">
          <div className="text-brand-muted text-sm">{t('settings.referral.referred')}</div>
          <div className="text-3xl font-bold text-brand-primary">{data.referredCount}</div>
        </div>
        <div className="card">
          <div className="text-brand-muted text-sm">{t('settings.referral.earned')}</div>
          <div className="text-3xl font-bold text-brand-primary">${data.earnedUsd.toFixed(2)}</div>
        </div>
      </div>
      <div className="card">
        <h2 className="text-lg font-semibold mb-3">{t('settings.referral.payouts')}</h2>
        {data.payouts.length === 0 ? (
          <Empty msg={t('settings.referral.noPayouts')} />
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {data.payouts.map((p) => (
                <tr key={p.id} className="border-b border-slate-100">
                  <td className="py-2">{dayjs(p.paidAt).format('YYYY-MM-DD')}</td>
                  <td className="py-2 text-right font-semibold">${p.amountUsd.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ===================== DANGER ZONE ===================== */

function DangerZoneTab(): JSX.Element {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [typed, setTyped] = useState('');

  // Синхронный JSON-дамп (не async job + email — см. api-gateway/src/dsar.ts).
  const exportMut = useMutation({
    mutationFn: async () => {
      const res = await api.post('/users/export', undefined, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'teleclaw-export.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success(t('settings.danger.exportQueued')),
    onError: () => toast.error(t('common.error')),
  });

  const deleteMut = useMutation({
    mutationFn: async () => api.delete('/users/account'),
    onSuccess: () => {
      toast.success(t('settings.danger.deleted'));
      setStep(0);
      void signOut();
    },
    onError: () => toast.error(t('common.error')),
  });

  return (
    <div className="card border-red-300 border-2">
      <h2 className="text-lg font-semibold text-red-700 mb-4 flex items-center gap-2">
        <AlertTriangle size={20} /> {t('settings.danger.title')}
      </h2>

      <div className="border-b border-slate-200 pb-4 mb-4">
        <div className="font-medium mb-1">{t('settings.danger.exportTitle')}</div>
        <p className="text-sm text-brand-muted mb-2">{t('settings.danger.exportDesc')}</p>
        <button className="btn-secondary" onClick={() => exportMut.mutate()} disabled={exportMut.isPending}>
          {t('settings.danger.exportBtn')}
        </button>
      </div>

      <div>
        <div className="font-medium mb-1 text-red-700">{t('settings.danger.deleteTitle')}</div>
        <p className="text-sm text-brand-muted mb-2">{t('settings.danger.deleteDesc')}</p>
        {step === 0 && (
          <button className="btn-secondary text-red-600 border-red-300" onClick={() => setStep(1)}>
            {t('settings.danger.deleteBtn')}
          </button>
        )}
        {step === 1 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('settings.danger.confirm1')}</p>
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setStep(0)}>{t('common.cancel')}</button>
              <button className="btn-primary bg-red-600 hover:bg-red-700" onClick={() => setStep(2)}>
                {t('settings.danger.confirmYes')}
              </button>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('settings.danger.typeDelete')}</p>
            <input
              className="input w-full"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
            />
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => { setStep(0); setTyped(''); }}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary bg-red-600 hover:bg-red-700"
                disabled={typed !== 'DELETE' || deleteMut.isPending}
                onClick={() => deleteMut.mutate()}
              >
                {t('settings.danger.deleteFinal')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===================== Shared UI ===================== */

function Field({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      {children}
    </div>
  );
}

function Badge({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-medium">
      {children}
    </span>
  );
}

function Empty({ msg }: { msg: string }): JSX.Element {
  return <div className="text-brand-muted text-sm py-6 text-center">{msg}</div>;
}

function EmptyAction({ msg, actionLabel, onAction }: { msg: string; actionLabel: string; onAction: () => void }): JSX.Element {
  return (
    <div className="text-center py-8 space-y-3">
      <p className="text-brand-muted text-sm">{msg}</p>
      <button className="btn-primary inline-flex items-center gap-2" onClick={onAction}>
        <Plus size={16} /> {actionLabel}
      </button>
    </div>
  );
}

function Loader(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="card flex items-center justify-center gap-2 text-brand-muted">
      <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
    </div>
  );
}

function ErrorBox(): JSX.Element {
  const { t } = useTranslation();
  return <div className="card text-red-600">{t('common.error')}</div>;
}

function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} aria-label="close" className="text-brand-muted hover:text-brand-ink">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
